import { DateTime } from 'luxon';
import { init_as_module as initAsModule } from 'mixpanel-browser/src/mixpanel-core';

import { featureFlagsSelectors, featureFlagsSimplifi } from 'app-components/flux/feature-flags';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { getBuildConfig } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { getTTI } from 'utils/tti';
import { BUILD_TYPE, Config } from './config';

import platform from './platform';

export const loadSessionRecorder = () => import('mixpanel-browser/src/recorder');

// Same as the package default entry (src/loaders/loader-module.js), except the session recorder
// and targeting bundles load as async chunks when Mixpanel asks for them instead of up front.
// Like the script-tag loader, a failed load never calls onload.
const mixpanel = initAsModule((src, onload) => {
  const load = /targeting/i.test(src) // src ends in __MP_TARGETING_FILENAME__ or __MP_RECORDER_FILENAME__
    ? import('mixpanel-browser/src/targeting')
    : loadSessionRecorder();
  // biome-ignore lint/suspicious/noConsole: tracking failures only log; they mustn't reach users
  load.then(() => onload()).catch((err) => console.error('Mixpanel bundle failed to load', src, err));
});

const clientType = 'Simplifi Web App';
const mixpanelToken = Config?.builds?.[BUILD_TYPE]?.mixpanel_token || 'f02e19059c63af74c567d59435e16a1d'; // test environment by default
mixpanel.init(mixpanelToken, {
  cookie_name: 'web_app',
  cross_subdomain_cookie: false,
  persistence: 'localStorage',
  persistence_name: 'web_app',
  ignore_dnt: true,
});
export const initialApiHost = mixpanel.get_config('api_host');
const isProxyEnabled = () => {
  const mixpanelProxy = store
    ? featureFlagsSelectors.getFeatureFlagEnsured(store.getState(), 'mixpanelProxy')
    : undefined;
  return typeof mixpanelProxy === 'boolean'
    ? mixpanelProxy
    : featureFlagsSimplifi.featureFlagsSimplifiDefaults.mixpanelProxy;
};
const proxyApiHost = getBuildConfig()?.mixpanelProxy;
assert(proxyApiHost, 'mixpanelProxy configuration not found');
if (isProxyEnabled() && proxyApiHost) {
  mixpanel.set_config({ api_host: proxyApiHost });
}

const deviceType = platform.device?.type || (platform.device.type === 'tablet' && 'iPad') || 'desktop';

// Initialize ischomebook TWA prop.
let isChromebookTWA = false;
const isChromebook = async () => {
  try {
    isChromebookTWA = await platform?.isChromebook?.();
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: tracking failures only log; they mustn't reach users
    console.error('Failed to check isChromebook', err);
  }
};

isChromebook();

// Mixpanel handles $device and $model automatically
mixpanel.register({
  client_type: clientType,
  quicken_year: DateTime.local().year,
  $app_version_string: process.env.APP_VERSION,
  $app_build_number: process.env.BUILD_NUMBER,
  pwa: window?.matchMedia?.('(display-mode: standalone)').matches,
  ...(deviceType ? { $device: deviceType } : undefined), // mixpanel has issues with $device detection for desktop and iPad - overriding it
});

export const dynamicDefaultPropertiesCreator = () => {
  const infoProps = mixpanel._?.info?.properties?.();
  const persistedProps = mixpanel.persistence?.props;
  const unpersistedSuperProps = mixpanel.unpersisted_superprops;
  const dynamicDefaultProps = {
    ...infoProps,
    ...persistedProps,
    ...unpersistedSuperProps,
  };
  const transactions = store ? transactionsSelectors.getResourcesById(store.getState()) : null;
  dynamicDefaultProps.sec_gpc = navigator?.globalPrivacyControl;
  dynamicDefaultProps.dnt = navigator?.doNotTrack;
  dynamicDefaultProps.tti = getTTI();
  dynamicDefaultProps.transactions = transactions?.size;
  dynamicDefaultProps.proxy = isProxyEnabled();
  dynamicDefaultProps.pathname = window?.location.pathname;
  dynamicDefaultProps.pageLocation = document.title.replace('Simplifi - ', '');
  if (typeof window !== 'undefined') {
    if (typeof window.outerWidth === 'number') {
      dynamicDefaultProps.browser_width = window.outerWidth;
      dynamicDefaultProps.browser_height = window.outerHeight;
    }
    if (typeof window.innerWidth === 'number') {
      dynamicDefaultProps.viewport_width = window.innerWidth;
      dynamicDefaultProps.viewport_height = window.innerHeight;
    }
  }
  dynamicDefaultProps.memory_device =
    typeof navigator.deviceMemory === 'number' ? navigator.deviceMemory * 1024 ** 3 : undefined;
  dynamicDefaultProps.memory_limit = window?.performance?.memory?.jsHeapSizeLimit || undefined;
  dynamicDefaultProps.memory_used = window?.performance?.memory?.usedJSHeapSize;
  if (
    typeof window?.performance?.memory?.jsHeapSizeLimit === 'number' &&
    typeof window?.performance?.memory?.usedJSHeapSize === 'number'
  ) {
    dynamicDefaultProps.memory_available =
      window?.performance?.memory?.jsHeapSizeLimit - window?.performance?.memory?.usedJSHeapSize;
  } else {
    dynamicDefaultProps.memory_available = undefined;
  }
  dynamicDefaultProps.is_chromebook_twa = isChromebookTWA;

  if (store) {
    dynamicDefaultProps.features = featureFlagsSelectors.getEnabledFeatureFlags(store.getState());
  }

  dynamicDefaultProps.online = navigator.onLine;

  return dynamicDefaultProps;
};

export default mixpanel;
